/**
 * Shared native-navigation for a SearchItem — one place that knows where each
 * content type's own detail page lives, so the sidebar recents rows and the
 * dashboard's Recent/Starred library rows (DashboardManager.js) never drift
 * apart on routing.
 *
 * Extracted from `SidebarRecentsController._navigateToItem`, which now
 * delegates here after resolving its (type, id) pair to a SearchItem. The
 * dashboard passes its already-resolved item directly. Neither caller ever
 * passes a `thread` item (open threads are sidebar-only — `THREAD_SOURCE`
 * stays out of the shared `searchSources`/`PRACTICE_SOURCE` set), but the
 * branch is kept here since the sidebar still needs it.
 *
 * @module components/contentNavigation
 */

import page from '../vendor/page.js';

/**
 * Open a SearchItem on its NATIVE detail surface — each content type routes to
 * its own page, not a single catch-all:
 *  - session → `/search?session=N` (the sessions page *is* /search; there's
 *    no separate per-session page, so this is its native surface).
 *  - note (partner note) → the native `/notes` page (`?note=<id>`), NOT
 *    /search. `list` (sent/received) + `state` (active/archived) are set from
 *    the item so /notes loads the list the note actually lives in before
 *    opening it — PartnerNotesSection.showDetailView only finds a note that's
 *    in the currently-loaded list/state, and a sent or archived note would
 *    otherwise fail to open under the received/active defaults.
 *  - thread (open thread) → `/discovery?thread=<threadNumber>`, the Open
 *    Threads tab's native detail route. `threadStatus` is set from the
 *    thread's current lifecycle state so DiscoverySection reloads under the
 *    matching status tab before resolving the deep link — a starred thread
 *    that's since been explored or archived otherwise never resolves under
 *    the Open tab's default 'unaddressed' load.
 *
 * `guide` routes to `/library?tab=guides&guide=<guideDefinitionId>` — the
 * Library Guides tab is its native surface (same target the dashboard
 * activity rows and the chat announcement card deep-link to).
 *
 * `practice` routes to `/experiments?status=<status>&practice=<practiceNumber>`
 * — BOTH params are required: `PracticesSection.applyURLParams` resolves
 * `?practice=` only by finding a match in the currently-loaded status
 * filter (defaults to 'active'), so a completed/archived practice's row
 * wouldn't be found without also passing `?status=` to load the right
 * filter first.
 * @param {Object} item - SearchItem (see searchSources.js's shape doc)
 * @param {Object} [opts]
 * @param {boolean} [opts.replace=false] - Replace the current history entry
 *   instead of pushing one. For a REDIRECT (a legacy deep link landing on a
 *   page that no longer opens that item itself): pushing would leave the
 *   redirecting URL one Back press away, which redirects again.
 */
export function openContentItem(item, { replace = false } = {}) {
  if (!item) return;
  const { type } = item;
  const go = (target) => (replace ? page.replace(target) : page(target));

  if (type === 'session') {
    const sessionNumber = item.payload?.session?.sessionNumber;
    if (!Number.isFinite(sessionNumber)) return;
    go(`/search?session=${sessionNumber}`);
  } else if (type === 'note') {
    const params = new URLSearchParams();
    if (item.payload?.direction === 'sent') params.set('list', 'sent');
    if (item.archived) params.set('state', 'archived');
    params.set('note', String(item.id));
    go(`/notes?${params.toString()}`);
  } else if (type === 'thread') {
    const threadNumber = item.payload?.threadNumber;
    if (!Number.isFinite(threadNumber)) return;
    const params = new URLSearchParams();
    const isDismissed = item.archived || Boolean(item.payload?.archivedAt);
    const status = isDismissed ? 'dismissed' : (item.payload?.addressedAt ? 'addressed' : 'unaddressed');
    if (status !== 'unaddressed') params.set('threadStatus', status);
    params.set('thread', String(threadNumber));
    go(`/discovery?${params.toString()}`);
  } else if (type === 'guide') {
    go(`/library?tab=guides&guide=${encodeURIComponent(item.id)}`);
  } else if (type === 'practice') {
    const practiceNumber = item.payload?.practiceNumber;
    const status = item.payload?.status;
    if (!Number.isFinite(practiceNumber) || !status) return;
    go(`/experiments?status=${encodeURIComponent(status)}&practice=${practiceNumber}`);
  }
}
